import { useStorePlanningWeek } from '@gozoki/api'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import chevronLeft from '~/assets/dashboard/chevron-left.svg'
import chevronRight from '~/assets/dashboard/chevron-right.svg'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { PlanningGrid } from './planning/PlanningGrid'
import { PlanningLegend } from './planning/PlanningLegend'
import { CalendarIcon } from './planning/planning-icons'

/** Garde-fou aligné sur celui du backend, qui retombe sur la semaine en cours au-delà. */
const MAX_WEEK_OFFSET = 52

function NavButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled: boolean
}) {
  return (
    // p/-m : cible tactile 44px (28px + 8px de padding), pastille 28px inchangée (kiosk PCAP).
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="shrink-0 p-[8px] -m-[8px] disabled:opacity-40 not-disabled:cursor-pointer"
    >
      <span className="flex size-[28px] items-center justify-center rounded-[14px] border border-[#D7D7D7] bg-white">
        <img src={icon} alt="" aria-hidden className="size-[14px]" />
      </span>
    </button>
  )
}

/**
 * Modale « Planning semaine en cours » (Figma 11393:41571), ouverte depuis le chevron du widget.
 *
 * Même charte que le widget : réutilise `PlanningGrid` (rail d'heures + colonnes `PlanningDayColumn`,
 * journée complète défilable). Ajoute le titre, la légende partagée et la navigation de semaine.
 * Rendue dans le canvas mis à l'échelle comme les autres modales du dashboard.
 */
export function PlanningWeekModal({ onClose }: { onClose: () => void }) {
  // `null` (offset 0) = semaine en cours : même clé de requête que le widget, donc aucun appel à
  // l'ouverture.
  const [weekOffset, setWeekOffset] = useState(0)
  const weekStart =
    weekOffset === 0
      ? undefined
      : (DateTime.now()
          .setZone('Europe/Paris')
          .startOf('week')
          .plus({ weeks: weekOffset })
          .toISODate() ?? undefined)

  const { data, isLoading } = useStorePlanningWeek(weekStart)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const shownWeek = data ? DateTime.fromISO(data.weekStart) : null
  const title =
    weekOffset === 0
      ? 'Planning semaine en cours'
      : `Planning — Semaine du ${shownWeek?.toFormat('dd/MM/yyyy') ?? ''}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#072C1A40] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label="Planning de la semaine"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-[1374px] max-w-full flex-col gap-[20px] overflow-hidden rounded-[20px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête : titre à gauche, légende puis fermeture à droite. */}
        <div className="flex items-center gap-[16px]">
          <div className="flex shrink-0 items-center gap-[8px]">
            <span className="flex size-[40px] items-center justify-center rounded-[12px] bg-[#F9F8F1] text-[#4F6A47]">
              <CalendarIcon />
            </span>
            <h2 className="font-brand text-[22px] leading-[26px] font-semibold whitespace-nowrap text-[#4F6A47] uppercase">
              {title}
            </h2>
          </div>

          <span className="h-px min-w-0 flex-1 bg-[#0000000F]" />

          <PlanningLegend />

          <ModalCloseButton onClose={onClose} ariaLabel="Fermer le planning" />
        </div>

        {/* Navigation de semaine. */}
        <div className="flex shrink-0 items-center gap-[14px]">
          <NavButton
            icon={chevronLeft}
            label="Semaine précédente"
            onClick={() => setWeekOffset((offset) => offset - 1)}
            disabled={weekOffset <= -MAX_WEEK_OFFSET}
          />
          <span className="font-sans text-[14px] font-semibold whitespace-nowrap text-[#072C1A]">
            {shownWeek ? `Semaine ${shownWeek.weekNumber}` : '—'}
          </span>
          <NavButton
            icon={chevronRight}
            label="Semaine suivante"
            onClick={() => setWeekOffset((offset) => offset + 1)}
            disabled={weekOffset >= MAX_WEEK_OFFSET}
          />
        </div>

        {data ? (
          <PlanningGrid days={data.days} affluenceMax={data.affluenceMax} />
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center font-sans text-[14px] text-[#8A8676]">
            {isLoading ? 'Chargement…' : 'Aucune donnée'}
          </div>
        )}
      </div>
    </div>
  )
}
